// src/components/Auth/InviteModal.tsx
import React, { useState } from 'react';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { useInviteCode } from '../../hooks/useInviteCode';

interface InviteModalProps {
  email?: string;
  onSuccess: (token: string) => void; // Updated to pass token
  onBack?: () => void;
}

export function InviteModal({ email, onSuccess, onBack }: InviteModalProps) {
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState('');
  const { validateInviteCode, isLoading, error: apiError } = useInviteCode();
  
  const error = localError || apiError;

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (!code.trim()) {
      setLocalError('Please enter your invite code');
      return;
    }

    try {
      const result = await validateInviteCode(code.trim());
      
      // In InviteModal.tsx
      if (result.success && result.sessionToken) {
        onSuccess(result.sessionToken);
      } else if (result.errorCode === 'CODE_EXPIRED') {
        setLocalError('This invite code has expired. Please request a new one.');
      } else if (result.errorCode === 'CODE_USED') {
        setLocalError('This code was already used. Please continue to login or request a new code.');
      } else {
        setLocalError(result.message || 'Invalid invite code');
      }
    } catch (err) {
      setLocalError('Failed to validate invite code');
    }
  };

  return (
    <>
      {/* Header */}
      <div className="px-6 flex flex-col justify-center border-b border-gray-200" style={{ height: '90px' }}>
        <h2 className="text-gray-900" style={{
          fontSize: 'var(--auth-font-size-2xl)',
          fontWeight: 'var(--auth-font-weight-regular)',
          fontFamily: 'var(--auth-header-font-family)'
        }}>Enter Invite Code</h2>
        {/* <p className="text-sm text-gray-500 mt-1">
          Enter the code from your invitation email
        </p> */}
      </div>
      {/* <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Enter Invite Code</h2>
        <p className="text-sm text-gray-500 mt-1">
          Enter the code from your invitation email
        </p>
      </div> */}

      {/* Form */}
      <form onSubmit={handleVerifyCode} className="p-6 space-y-6">
        {email && (
          <div className="text-sm text-gray-600">
            <p>We've sent your invite code to <strong>{email}</strong></p>
          </div>
        )}
        {/* Code Input */}
        <div>
          {/* <label htmlFor="invite-code" className="block text-sm font-medium text-gray-700 mb-2">
            Invite Code
          </label> */}
          <input
            type="text"
            id="invite-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            style={{
              height: 'var(--auth-button-height)',
              padding: 'var(--auth-button-padding)',
              fontSize: 'var(--auth-button-font-size)',
              fontWeight: 'var(--auth-button-font-weight)',
              borderRadius: 'var(--auth-button-border-radius)'
            }}
            placeholder="Enter your invite code (e.g. TRIP-123ABC)"
            disabled={isLoading}
            autoComplete="off"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {error && (error.includes('expired') || error.includes('already used')) && (
          <button
            type="button"
            onClick={onBack}
            className="w-full mt-2 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Request New Code
          </button>
        )}
        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className={`w-full py-2 px-4 rounded-md text-white font-medium flex items-center justify-center gap-2
            ${isLoading 
              ? 'bg-blue-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 transition-colors'
            }`
          }
          style={{ borderRadius: 'var(--auth-button-border-radius)' }}
        >
          {isLoading ? (
            <span>Verifying...</span>
          ) : (
            <>
              <span>Verify</span>
              <ArrowRight size={18} />
            </>
          )}
        </button>

        {/* Back Button */}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="w-full mt-2 py-2 px-4 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            style={{ borderRadius: 'var(--auth-button-border-radius)' }}

          >
            Back to Request
          </button>
        )}
      </form>
    </>
  );
}